import { Component, ChangeDetectionStrategy, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminService } from '../../services/admin.service';
import { SystemSettings, LogEntry, LogLevel } from '../../models/settings.model';
import { CorrectionWithUser } from '../../models/correction.model';
import { AuthService } from '../../services/auth.service';

type SettingsTab = 'integrations' | 'content' | 'logs' | 'moderation';

@Component({
  selector: 'app-settings',
  standalone: true,
  template: `
<div class="space-y-8">
  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <h2 class="text-2xl font-bold text-slate-900">Configurações Gerais</h2>
        <p class="text-sm text-gray-500">Gerencie as configurações e integrações do sistema.</p>
      </div>
    </div>

    <div class="border-b border-gray-200">
      <nav class="-mb-px flex space-x-6" aria-label="Tabs">
        <button (click)="setTab('integrations')" [class.border-custom-blue]="activeTab() === 'integrations'" [class.text-custom-blue]="activeTab() === 'integrations'" [class.border-transparent]="activeTab() !== 'integrations'" [class.text-gray-500]="activeTab() !== 'integrations'" [class.hover:text-gray-700]="activeTab() !== 'integrations'" [class.hover:border-gray-300]="activeTab() !== 'integrations'" class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm">Integrações</button>
        <button (click)="setTab('content')" [class.border-custom-blue]="activeTab() === 'content'" [class.text-custom-blue]="activeTab() === 'content'" [class.border-transparent]="activeTab() !== 'content'" [class.text-gray-500]="activeTab() !== 'content'" [class.hover:text-gray-700]="activeTab() !== 'content'" [class.hover:border-gray-300]="activeTab() !== 'content'" class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm">Conteúdo</button>
        <button (click)="setTab('logs')" [class.border-custom-blue]="activeTab() === 'logs'" [class.text-custom-blue]="activeTab() === 'logs'" [class.border-transparent]="activeTab() !== 'logs'" [class.text-gray-500]="activeTab() !== 'logs'" [class.hover:text-gray-700]="activeTab() !== 'logs'" [class.hover:border-gray-300]="activeTab() !== 'logs'" class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm">Logs do Sistema</button>
        <button (click)="setTab('moderation')" [class.border-custom-blue]="activeTab() === 'moderation'" [class.text-custom-blue]="activeTab() === 'moderation'" [class.border-transparent]="activeTab() !== 'moderation'" [class.text-gray-500]="activeTab() !== 'moderation'" [class.hover:text-gray-700]="activeTab() !== 'moderation'" [class.hover:border-gray-300]="activeTab() !== 'moderation'" class="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative">
          Moderação
          @if(reportedCorrections().length > 0) {
            <span class="absolute top-2 -right-3 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{{ reportedCorrections().length }}</span>
          }
        </button>
      </nav>
    </div>

    <div class="pt-8">
      @if(isLoading()) {
        <p class="text-gray-500">Carregando configurações...</p>
      } @else {
        @if(settings(); as s) {
          <form (ngSubmit)="onSave()">
            @switch (activeTab()) {
              @case ('integrations') {
                <div class="space-y-6 max-w-2xl">
                  <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200">Provedores de Serviço</h3>
                  <div><label for="supabaseUrl" class="block text-sm font-medium text-gray-700">Supabase URL</label><input type="text" id="supabaseUrl" name="supabaseUrl" [value]="s.integrations.supabaseUrl" (input)="updateIntegration('supabaseUrl', $event)" class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 focus:border-custom-blue focus:ring-custom-blue sm:text-sm"></div>
                  <div><label for="supabaseKey" class="block text-sm font-medium text-gray-700">Supabase Anon Key</label><input type="password" id="supabaseKey" name="supabaseKey" [value]="s.integrations.supabaseKey" (input)="updateIntegration('supabaseKey', $event)" class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 focus:border-custom-blue focus:ring-custom-blue sm:text-sm"></div>
                  <div><label for="openAiApiKey" class="block text-sm font-medium text-gray-700">OpenAI API Key</label><input type="password" id="openAiApiKey" name="openAiApiKey" [value]="s.integrations.openAiApiKey" (input)="updateIntegration('openAiApiKey', $event)" class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 focus:border-custom-blue focus:ring-custom-blue sm:text-sm"></div>
                </div>
              }
              @case ('content') {
                <div class="space-y-6 max-w-3xl">
                  <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200">Textos da Aplicação</h3>
                  <div><label for="termsOfUse" class="block text-sm font-medium text-gray-700">Termos de Uso</label><textarea id="termsOfUse" name="termsOfUse" rows="8" [value]="s.content.termsOfUse" (input)="updateContent('termsOfUse', $event)" class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 focus:border-custom-blue focus:ring-custom-blue sm:text-sm"></textarea></div>
                  <div><label for="privacyPolicy" class="block text-sm font-medium text-gray-700">Política de Privacidade</label><textarea id="privacyPolicy" name="privacyPolicy" rows="8" [value]="s.content.privacyPolicy" (input)="updateContent('privacyPolicy', $event)" class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 focus:border-custom-blue focus:ring-custom-blue sm:text-sm"></textarea></div>
                  <div><label for="welcomeMessage" class="block text-sm font-medium text-gray-700">Mensagem de Boas-vindas</label><input type="text" id="welcomeMessage" name="welcomeMessage" [value]="s.content.defaultWelcomeMessage" (input)="updateContent('defaultWelcomeMessage', $event)" class="mt-1 block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2.5 text-slate-800 dark:text-gray-200 focus:border-custom-blue focus:ring-custom-blue sm:text-sm"></div>
                </div>
              }
              @case ('logs') {
                <div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">Logs Recentes</h3>
                  <div class="overflow-x-auto border border-gray-200  rounded-xl max-h-96">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-custom-gray sticky top-0">
                        <tr>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nível</th>
                          <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mensagem</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        @for(log of logs(); track $index) {
                          <tr>
                            <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">{{ log.created_at | date:'yyyy-MM-dd HH:mm:ss' }}</td>
                            <td class="px-4 py-3 whitespace-nowrap"><span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" [class]="getLogLevelClass(log.level)">{{ log.level }}</span></td>
                            <td class="px-4 py-3 text-sm text-gray-800 font-mono">{{ log.message }}</td>
                          </tr>
                        } @empty {
                          <tr><td colspan="3" class="px-4 py-4 text-center text-gray-500">Nenhum log encontrado.</td></tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              }
              @case('moderation') {
                <div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">Moderação de Conteúdo</h3>
                  @if (reportedCorrections().length > 0) {
                    <div class="space-y-4">
                      @for (correction of reportedCorrections(); track correction.id) {
                        <div class="border border-gray-200 rounded-lg">
                          <button type="button" (click)="toggleCorrection(correction.id)" class="w-full text-left p-4 flex justify-between items-center bg-custom-gray hover:bg-gray-200">
                            <div>
                              <p class="font-semibold text-custom-black">Aluno: <span class="font-normal">{{ correction.profiles?.name || '-' }} ({{ correction.profiles?.email || '-' }})</span></p>
                              <p class="text-sm text-gray-600">Data: {{ correction.created_at | date:'dd/MM/yyyy' }} - Motivo: <span class="italic font-medium">"{{ correction.report_reason }}"</span></p>
                            </div>
                            <svg class="w-5 h-5 transform transition-transform" [class.rotate-180]="expandedCorrectionId() === correction.id" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" /></svg>
                          </button>
                          @if(expandedCorrectionId() === correction.id) {
                            <div class="p-4 space-y-4">
                              <div class="border-t pt-4">
                                <h4 class="font-semibold text-sm mb-2">Reescrita Sugerida (Conteúdo da Denúncia)</h4>
                                <div class="prose prose-sm max-w-none bg-gray-100 p-3 rounded-md max-h-60 overflow-y-auto whitespace-pre-wrap">
                                  {{ getReportedRewrite(correction) }}
                                </div>
                              </div>
                              <div class="flex justify-end space-x-3">
                                <button type="button" (click)="resolveReport(correction.id, 'reviewed_ok')" class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100">Dispensar Denúncia</button>
                                <button type="button" (click)="resolveReport(correction.id, 'reviewed_removed')" class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700">Remover Redação</button>
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="text-center bg-custom-gray p-12 rounded-lg">
                      <p class="font-semibold text-gray-800">Tudo em ordem!</p>
                      <p class="text-sm text-gray-600 mt-2">Não há nenhuma redação denunciada para revisar no momento.</p>
                    </div>
                  }
                </div>
              }
            }

            @if (activeTab() === 'integrations' || activeTab() === 'content') {
              <div class="mt-8 pt-6 border-t flex justify-end items-center">
                @if(saveStatus() === 'success') {
                  <span class="text-green-600 font-medium text-sm mr-4">Alterações salvas com sucesso!</span>
                }
                <button type="submit" [disabled]="saveStatus() === 'saving'" class="flex items-center justify-center bg-custom-blue text-white font-semibold py-2.5 px-5 rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed hover:bg-indigo-700">
                  @if (saveStatus() === 'saving') {
                    <div class="loader !w-5 !h-5 !border-2 !border-t-white"></div>
                    <span class="ml-2">Salvando...</span>
                  } @else {
                    <span>Salvar Alterações</span>
                  }
                </button>
              </div>
            }
          </form>
        } @else {
          <div class="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-r-lg" role="alert">
            <p class="font-bold">Erro</p>
            <p>Não foi possível carregar as configurações do sistema.</p>
          </div>
        }
      }
    </div>
  </div>
</div>
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule]
})
export class SettingsComponent implements OnInit {
  private adminService = inject(AdminService);
  private authService = inject(AuthService);

  readonly activeTab = signal<SettingsTab>('integrations');
  readonly settings = signal<SystemSettings | null>(null);
  readonly logs = signal<LogEntry[]>([]);
  readonly reportedCorrections = signal<CorrectionWithUser[]>([]);
  readonly isLoading = signal(true);
  readonly saveStatus = signal<'idle' | 'saving' | 'success'>('idle');
  readonly expandedCorrectionId = signal<number | null>(null);
  readonly pendingModerationAction = signal<string | null>(null);

  async ngOnInit() {
    this.isLoading.set(true);
    try {
      const [settingsData, logsData, reportedData] = await Promise.all([
        this.adminService.getSettings(),
        this.adminService.getSystemLogs(),
        this.adminService.getReportedCorrections(),
      ]);
      this.settings.set(settingsData);
      this.logs.set(logsData);
      this.reportedCorrections.set(reportedData);
    } catch (error) {
      console.error('Failed to load settings data', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  setTab(tab: SettingsTab) {
    this.activeTab.set(tab);
  }

  updateIntegration(field: 'supabaseUrl' | 'supabaseKey' | 'openAiApiKey', event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.settings.update(s => {
      if (!s) return s;
      return {
        ...s,
        integrations: { ...s.integrations, [field]: value }
      };
    });
  }

  updateContent(field: 'termsOfUse' | 'privacyPolicy' | 'defaultWelcomeMessage', event: Event) {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.settings.update(s => {
      if (!s) return s;
      return {
        ...s,
        content: { ...s.content, [field]: value }
      };
    });
  }

  async onSave() {
    if (!this.settings()) return;
    this.saveStatus.set('saving');
    try {
      await this.adminService.saveSettings(this.settings()!);
      this.saveStatus.set('success');
      setTimeout(() => this.saveStatus.set('idle'), 3000);
    } catch (error) {
      console.error('Failed to save settings', error);
      this.saveStatus.set('idle');
    }
  }

  getLogLevelClass(level: LogLevel): string {
    switch (level) {
      case 'INFO': return 'bg-blue-100 text-blue-800';
      case 'WARN': return 'bg-yellow-100 text-yellow-800';
      case 'ERROR': return 'bg-red-100 text-red-800';
    }
  }

  toggleCorrection(id: number) {
    this.expandedCorrectionId.update(currentId => currentId === id ? null : id);
  }

  async resolveReport(id: number, status: 'reviewed_ok' | 'reviewed_removed') {
    const actionKey = `${id}:${status}`;
    if (this.pendingModerationAction() !== actionKey) {
      this.pendingModerationAction.set(actionKey);
      this.authService.showNotification(
        'info',
        'Confirmar moderação',
        status === 'reviewed_removed'
          ? 'Clique novamente em até 5 segundos para remover esta redação.'
          : 'Clique novamente em até 5 segundos para dispensar esta denúncia.'
      );
      setTimeout(() => {
        if (this.pendingModerationAction() === actionKey) {
          this.pendingModerationAction.set(null);
        }
      }, 5000);
      return;
    }

    this.pendingModerationAction.set(null);

    try {
      await this.adminService.resolveCorrectionReport(id, status);
      this.reportedCorrections.update(corrections => corrections.filter(c => c.id !== id));
      this.authService.showNotification(
        'success',
        'Moderação concluída',
        status === 'reviewed_removed'
          ? 'A redação foi removida com sucesso.'
          : 'A denúncia foi dispensada com sucesso.'
      );
    } catch (error) {
      console.error(`Failed to resolve report for correction ${id}`, error);
      this.authService.showNotification('error', 'Erro na moderação', 'Não foi possível processar a solicitação.');
    }
  }
}

